import { cn } from '@lib/cn'
import NextImage, { ImageProps as NextImageProps } from 'next/image'
import { FC } from 'react'
import { theme } from '../theme'

export type ImageProps = Omit<NextImageProps, 'src'> & {
  sources: string[]
  alt: string
  width?: number
  height?: number
  priority?: boolean
  objectFit?: 'cover' | 'contain'
  className?: string
  style?: React.CSSProperties
  onLoad?: () => void
}

export const Image: FC<ImageProps> = ({
  sources,
  alt,
  width,
  height,
  priority = false,
  objectFit = 'cover',
  className,
  style,
  onLoad,
  ...rest
}) => {
  // Use the biggest source as the fallback image
  if (!sources.length) {
    return null
  }

  return (
    <div className={cn('relative flex w-full overflow-hidden', className)} style={style}>
      {/* Responsive image with picture element */}
      <picture className="absolute left-0 top-0 z-[2] h-full w-full">
        {/* Generate source elements for different breakpoints */}
        {sources
          .map((source, index) => ({
            source,
            breakpoint: theme.breakpoints[index],
          }))
          .reverse() // Ensure largest breakpoint comes first
          .map(({ source, breakpoint }, index) => (
            <source
              key={index}
              media={breakpoint ? `(min-width: ${breakpoint})` : undefined}
              srcSet={source}
            />
          ))}

        <NextImage
          src={sources[0]}
          alt={alt}
          {...(width && height ? { width, height } : { fill: true })}
          className="h-full w-full"
          style={{ objectFit }}
          priority={priority}
          onLoad={onLoad}
          {...rest}
        />
      </picture>
    </div>
  )
}
